/** RaiderMapCR - Floor plan hook: loads SVGs, room data, and provides room utilities */
import { useState, useEffect, useCallback } from "react";

const useFloorPlan = (floor = "F1") => {
  const [allFloorsData, setAllFloorsData] = useState({});
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [lastLoggedData, setLastLoggedData] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  useEffect(() => {
    const loadAllFloors = async () => {
      try {
        setLastLoggedData(null);
        
        const floors = ['F1', 'F3', 'S1'];
        
        const floorPromises = floors.map(async (floorName) => {
          const [svgResponse, apiResponse, iconsResponse] = await Promise.all([
            fetch(`/images/floor-plans/${floorName.toLowerCase()}_v1.svg`),
            fetch(`/api/rooms?floor=${floorName}`),
            (floorName === 'F1' || floorName === 'S1' || floorName === 'F3') ? fetch(`/images/floor-icons/${floorName.toLowerCase()}_v1.svg`) : Promise.resolve({ ok: false })
          ]);

          let svgText = "";
          let roomData = { rooms: {} };
          let iconsText = "";
          let issues = [];

          if (svgResponse.ok) {
            svgText = await svgResponse.text();
          } else {
            issues.push(`SVG missing (${svgResponse.status})`);
          }

          if ((floorName === 'F1' || floorName === 'S1' || floorName === 'F3') && iconsResponse.ok) {
            iconsText = await iconsResponse.text();
          }
          
          if (apiResponse.ok) {
            const rooms = await apiResponse.json();
            const roomsObj = {};
            rooms.forEach(room => {
              roomsObj[room.path_id] = {
                dbId: room.id,
                name: room.name,
                type: room.type,
                capacity: room.capacity,
                status: room.status,
                alias: room.alias,
                default_status: room.default_status,
                path_id: room.path_id,
                has_label: room.has_label,
                label_x: room.label_x,
                label_y: room.label_y,
                label: room.label,
                label_size: room.label_size
              };
            });
            roomData = { rooms: roomsObj };
          } else {
            issues.push(`API missing (${apiResponse.status})`);
          }

          if (issues.length > 0) {
            console.warn(`🚨 ${floorName}: ${issues.join(', ')}`);
          }

          return { floor: floorName, svgText, roomData, iconsText };
        });

        const allFloorData = await Promise.all(floorPromises);
        
        const processedData = {};
        allFloorData.forEach(({ floor: floorName, svgText, roomData, iconsText }) => {
          processedData[floorName] = { svgText, roomData, iconsText };
        });

        setAllFloorsData(processedData);
        setIsFullyLoaded(true);
        setError(null);
      } catch (error) {
        console.error('❌ Load failed -', error.message);
        setError('Failed to load floor data');
        setIsFullyLoaded(true);
      }
    };

    setIsFullyLoaded(false);
    setAllFloorsData({}); // Clear all floor data immediately
    loadAllFloors();
  }, [reloadToken]);

  const refreshRoomData = useCallback(async () => {
    if (!isFullyLoaded) return;
    
    try {
      const floors = ['F1', 'F3', 'S1'];
      
      const roomPromises = floors.map(async (floorName) => {
        const apiResponse = await fetch(`/api/rooms?floor=${floorName}`);
        
        if (apiResponse.ok) {
          const rooms = await apiResponse.json();
          const roomsObj = {};
          rooms.forEach(room => {
            roomsObj[room.path_id] = {
              dbId: room.id,
              name: room.name,
              type: room.type,
              capacity: room.capacity,
              status: room.status,
              alias: room.alias,
              default_status: room.default_status,
              path_id: room.path_id,
              has_label: room.has_label,
              label_x: room.label_x,
              label_y: room.label_y,
              label: room.label,
              label_size: room.label_size
            };
          });
          return { floor: floorName, roomData: { rooms: roomsObj } };
        } else {
          console.warn(`⚠️ Failed to refresh room data for ${floorName}`);
          return null;
        }
      });

      const refreshedData = await Promise.all(roomPromises);
      
      setAllFloorsData(prevData => {
        const updatedData = { ...prevData };
        refreshedData.forEach(data => {
          if (data && updatedData[data.floor]) {
            updatedData[data.floor] = {
              ...updatedData[data.floor],
              roomData: data.roomData
            };
          }
        });
        return updatedData;
      });
      
      console.log('✅ Room data refreshed (SVGs preserved)');
    } catch (error) {
      console.error('❌ Room data refresh failed:', error.message);
    }
  }, [isFullyLoaded]);

  const [rooms, setRooms] = useState([]);
  
  useEffect(() => {
    setRooms([]);
    
    if (!isFullyLoaded || !allFloorsData[floor]) {
      return;
    }

    const { svgText, roomData } = allFloorsData[floor];
    
    if (!svgText || !roomData.rooms) {
      return;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, "image/svg+xml");
      
      const parseError = doc.querySelector("parsererror");
      if (parseError) {
        throw new Error("SVG parsing error: " + parseError.textContent);
      }
      
      const pathElements = doc.querySelectorAll("path");
      const roomList = [];
      let matchedPaths = 0;
      let unmatchedPaths = [];
      let pathsWithoutId = 0;
      
      const totalSvgPaths = pathElements.length;
      const totalJsonRooms = Object.keys(roomData.rooms || {}).length;
      
      Array.from(pathElements).forEach((path) => {
        const pathId = path.id;
        
        if (!pathId) {
          pathsWithoutId++;
          return;
        }
        
        const fullPathId = `${floor}_${pathId}`;
        
        if (roomData.rooms[fullPathId]) {
          const pathData = path.getAttribute("d");
          roomList.push({
            id: pathId,
            label: pathId,
            pathData: pathData,
            ...roomData.rooms[fullPathId]
          });
          matchedPaths++;
        } else {
          unmatchedPaths.push(pathId);
        }
      });

      const jsonRoomIds = Object.keys(roomData.rooms || {});
      const svgPathIds = Array.from(pathElements).map(p => p.id).filter(id => id);
      const svgPathIdsWithFloor = svgPathIds.map(id => `${floor}_${id}`);
      const orphanedJsonRooms = jsonRoomIds.filter(id => !svgPathIdsWithFloor.includes(id));

      const dataSignature = `${floor}-${totalSvgPaths}-${totalJsonRooms}-${matchedPaths}-${unmatchedPaths.length}-${orphanedJsonRooms.length}-${pathsWithoutId}`;
      const isCompleteMismatch = totalSvgPaths > 0 && totalJsonRooms > 0 && matchedPaths === 0 &&
                               orphanedJsonRooms.length === totalJsonRooms;
      const isPartialData = totalSvgPaths === 0 || totalJsonRooms === 0;
      
      const shouldLog = lastLoggedData !== dataSignature && 
                       !isCompleteMismatch && 
                       !isPartialData;
      
      if (shouldLog) {
        const hasIssues = unmatchedPaths.length > 0 || orphanedJsonRooms.length > 0 || pathsWithoutId > 0;
        const statusIcon = hasIssues ? '⚠️' : '✅';
        const statusText = hasIssues ? 'Data mismatch detected' : 'Perfect match - all synchronized';
        
        console.log(`📊 ${floor}: ${statusIcon} ${statusText} | SVG:${totalSvgPaths} JSON:${totalJsonRooms} Matched:${matchedPaths} | SVG-orphans:${unmatchedPaths.length} JSON-orphans:${orphanedJsonRooms.length} NoID:${pathsWithoutId}`);
        
        if (unmatchedPaths.length > 0) {
          console.log(`   🔍 Unmatched SVG: ${unmatchedPaths.slice(0, 5).join(', ')}${unmatchedPaths.length > 5 ? ` (+${unmatchedPaths.length - 5} more)` : ''}`);
        }
        
        if (orphanedJsonRooms.length > 0) {
          console.log(`   🔍 Orphaned JSON: ${orphanedJsonRooms.slice(0, 5).join(', ')}${orphanedJsonRooms.length > 5 ? ` (+${orphanedJsonRooms.length - 5} more)` : ''}`);
        }
        setLastLoggedData(dataSignature);
      }
      
      const sortedRoomList = roomList.sort((a, b) => {
        if (a.type === 'structure' && b.type !== 'structure') return -1;
        if (b.type === 'structure' && a.type !== 'structure') return 1;
        return 0;
      });
      
      setRooms(sortedRoomList);
    } catch (error) {
      console.error("Error parsing SVG:", error);
      setError("Failed to parse SVG data: " + error.message);
    }
  }, [floor, allFloorsData, isFullyLoaded, lastLoggedData]);

  const getRoomColor = useCallback((room, viewMode = "occupancy") => {
    if (viewMode === "occupancy") {
      if (room.type === "structure") {
        return "#333333";
      }
      const statusColors = {
        available: "#A8DDA8",
        occupied: "#FFB3B3",
        unavailable: "#D4C5E2",
        unknown: "#9B9B9B"
      };
      return statusColors[room.status] || statusColors.unknown;
    } else {
      const typeColors = {
        laboratory: "#87CEEB",
        classroom: "#FFCB80",
        office: "#E6E6FA",
        study: "#FFFACD",
        service: "#DDA0DD",
        bathroom: "#F0E68C",
        stairs: "#B0C4DE",
        elevator: "#D2B48C",
        structure: "#333333",
        other: "#98D8C8"
      };
      return typeColors[room.type] || typeColors.other;
    }
  }, []);

  const getRoomById = (id) => {
    return rooms.find(room => room.id === id);
  };

  const getRoomsByStatus = (status) => {
    return rooms.filter(room => room.status === status);
  };

  const getRoomsByType = (type) => {
    return rooms.filter(room => room.type === type);
  };

  const getStats = () => {
    const accessibleRooms = rooms.filter(room => room.type !== "structure");
    
    const stats = {
      total: accessibleRooms.length,
      byStatus: {},
      byType: {}
    };

    accessibleRooms.forEach(room => {
      stats.byStatus[room.status] = (stats.byStatus[room.status] || 0) + 1;
      stats.byType[room.type] = (stats.byType[room.type] || 0) + 1;
    });

    return stats;
  };

  const makePathsVisible = useCallback((svgElement) => {
    if (!svgElement) return;
    
    const allPaths = svgElement.querySelectorAll("path");
    
    allPaths.forEach(path => {
      if (path.getAttribute('fill') === 'none') {
        path.removeAttribute('fill');
      }
      path.style.fill = "#e5e7eb";
      path.style.fillOpacity = "0.6";
      path.style.stroke = "#262C37";
      path.style.pointerEvents = "auto";
    });
  }, []);

  const applySvgColors = useCallback((svgElement, viewMode = "occupancy", currentRooms = rooms) => {
    if (!svgElement) return;
    makePathsVisible(svgElement);

    currentRooms.forEach(room => {
      const pathElement = svgElement.querySelector(`#${room.id}`);
      if (pathElement) {
        if (pathElement.getAttribute('fill') === 'none') {
          pathElement.removeAttribute('fill');
        }
        
        const color = getRoomColor(room, viewMode);
        pathElement.style.fill = color;
        pathElement.style.fillOpacity = room.type === "structure" ? "0.8" : "0.8";
        if (room.type === "structure") {
          pathElement.style.stroke = "#1A1A1A";
          pathElement.style.cursor = "default";
          pathElement.style.pointerEvents = "none";
        } else {
          pathElement.style.stroke = "#1A1A1A";
          pathElement.style.cursor = "pointer";
          pathElement.style.pointerEvents = "all";
        }
      }
    });
    
  }, [makePathsVisible, getRoomColor, rooms]);

  const adjustColorBrightness = useCallback((color, factor) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const newR = Math.min(255, Math.floor(r * factor));
    const newG = Math.min(255, Math.floor(g * factor));
    const newB = Math.min(255, Math.floor(b * factor));
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }, []);

  const applyLabelColors = useCallback((svgElement, viewMode = "occupancy", currentRooms = rooms, hoveredRoomId = null, selectedRoomId = null) => {
    if (!svgElement) return;

    const labelsOverlay = svgElement.querySelector('#labels-overlay');
    if (!labelsOverlay) return;

    const labelElements = labelsOverlay.querySelectorAll('text');

    labelElements.forEach(labelElement => {
      const roomId = labelElement.getAttribute('data-room-id');
      if (!roomId) return;

      const matchedRoom = currentRooms.find(room => room.id === roomId);
      if (!matchedRoom) return;

      const roomColor = getRoomColor(matchedRoom, viewMode);
      const isHighlighted = selectedRoomId === roomId;
      const isHovered = hoveredRoomId === roomId;
      if (isHighlighted || isHovered) {
        const labelColor = adjustColorBrightness(roomColor, 0.6);
        labelElement.setAttribute('fill', labelColor);
        labelElement.setAttribute('fill-opacity', '1');
        labelElement.style.filter = '';
        return;
      }
      const labelColor = adjustColorBrightness(roomColor, 1.1);
      labelElement.setAttribute('fill', labelColor);
      labelElement.setAttribute('fill-opacity', '1');
      labelElement.style.filter = '';
    });
  }, [rooms, getRoomColor, adjustColorBrightness]);

  const highlightRoom = useCallback((roomId, svgElement, viewMode = "occupancy") => {
    if (!svgElement) return;
    
    const pathElement = svgElement.querySelector(`#${roomId}`);
    if (pathElement) {
      if (!pathElement.classList.contains('room-path')) {
        pathElement.classList.add('room-path');
      }
      pathElement.classList.add('highlighted');
      const room = rooms.find(r => r.id === roomId);
      if (room) {
        const baseColor = getRoomColor(room, viewMode);
        const highlightColor = adjustColorBrightness(baseColor, 1.2);
        pathElement.style.fill = highlightColor;
        const labelsOverlay = svgElement.querySelector('#labels-overlay');
        if (labelsOverlay) {
          const labelElement = labelsOverlay.querySelector(`text[data-room-id="${roomId}"]`);
          if (labelElement) {
            labelElement.style.filter = '';
            const labelColor = adjustColorBrightness(baseColor, 0.6);
            labelElement.setAttribute('fill', labelColor);
          }
        }
      }
    }
  }, [rooms, getRoomColor, adjustColorBrightness]);

  const unhighlightRoom = useCallback((roomId, svgElement, viewMode = "occupancy") => {
    if (!svgElement) return;
    
    const pathElement = svgElement.querySelector(`#${roomId}`);
    if (pathElement) {
      pathElement.classList.remove('highlighted');
      const room = rooms.find(r => r.id === roomId);
      if (room) {
        const baseColor = getRoomColor(room, viewMode);
        pathElement.style.fill = baseColor;
        pathElement.style.strokeWidth = '1px';
        const labelsOverlay = svgElement.querySelector('#labels-overlay');
        if (labelsOverlay) {
          const labelElement = labelsOverlay.querySelector(`text[data-room-id="${roomId}"]`);
          if (labelElement) {
            const labelColor = adjustColorBrightness(baseColor, 1.1);
            labelElement.setAttribute('fill', labelColor);
          }
        }
      }
    }
  }, [rooms, getRoomColor, adjustColorBrightness]);

  const combineSvgWithIcons = useCallback((floorSvgText, iconsSvgText) => {
    if (!iconsSvgText || !floorSvgText) {
      return floorSvgText;
    }

    try {
      const parser = new DOMParser();
      
      // Parse the floor plan SVG
      const floorDoc = parser.parseFromString(floorSvgText, "image/svg+xml");
      const floorSvg = floorDoc.querySelector("svg");
      const iconsDoc = parser.parseFromString(iconsSvgText, "image/svg+xml");
      const iconsGroup = iconsDoc.querySelector("g");
      
      if (floorSvg && iconsGroup) {
        const iconsOverlay = floorDoc.createElementNS("http://www.w3.org/2000/svg", "g");
        iconsOverlay.setAttribute("id", "icons-overlay");
        iconsOverlay.setAttribute("style", "pointer-events: none; cursor: default; opacity: 0.8; z-index: 999;");
        const clonedIconsGroup = iconsGroup.cloneNode(true);
        iconsOverlay.appendChild(clonedIconsGroup);
        floorSvg.appendChild(iconsOverlay);
        return new XMLSerializer().serializeToString(floorDoc);
      }
    } catch (error) {
      console.warn("⚠️ Failed to combine SVG with icons:", error.message);
    }
    
    return floorSvgText;
  }, []);

  const combineSvgWithLabels = useCallback((svgText, roomsData) => {
    if (!svgText || !roomsData) {
      return svgText;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, "image/svg+xml");
      const svgElement = doc.querySelector("svg");
      
      if (!svgElement) return svgText;
      const tempContainer = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      tempContainer.style.position = "absolute";
      tempContainer.style.visibility = "hidden";
      tempContainer.style.pointerEvents = "none";
      document.body.appendChild(tempContainer);
      const tempSvg = svgElement.cloneNode(true);
      tempContainer.appendChild(tempSvg);
      const labelsOverlay = doc.createElementNS("http://www.w3.org/2000/svg", "g");
      labelsOverlay.setAttribute("id", "labels-overlay");
      labelsOverlay.setAttribute("style", "pointer-events: none; cursor: default;");
      Object.keys(roomsData).forEach(pathId => {
        const roomData = roomsData[pathId];
        if (roomData.has_label && roomData.label) {
          const svgPathId = pathId.replace(/^[^_]+_/, '');
          const tempPathElement = tempSvg.querySelector(`#${svgPathId}`);
          
          if (tempPathElement) {
            try {
              const bbox = tempPathElement.getBBox();
              const labelX = bbox.x + (roomData.label_x || 0);
              const labelY = bbox.y + (roomData.label_y || 0);
              const labelSize = roomData.label_size || 8;
              const textElement = doc.createElementNS("http://www.w3.org/2000/svg", "text");
              textElement.setAttribute("x", labelX);
              textElement.setAttribute("y", labelY);
              textElement.setAttribute("fill", "#888888");
              textElement.setAttribute("fill-opacity", "1");
              textElement.setAttribute("font-size", labelSize.toString());
              textElement.setAttribute("font-family", "Poppins, sans-serif");
              textElement.setAttribute("font-weight", "600");
              textElement.setAttribute("stroke", "none");
              textElement.setAttribute("dominant-baseline", "hanging");
              textElement.setAttribute("data-room-id", svgPathId);
              textElement.textContent = roomData.label;
              
              labelsOverlay.appendChild(textElement);
            } catch (error) {
              console.warn(`⚠️ Failed to add label for room ${pathId}:`, error.message);
            }
          }
        }
      });
      document.body.removeChild(tempContainer);
      if (labelsOverlay.children.length > 0) {
        svgElement.appendChild(labelsOverlay);
      }
      return new XMLSerializer().serializeToString(doc);
    } catch (error) {
      console.warn("⚠️ Failed to add labels to SVG:", error.message);
    }
    
    return svgText;
  }, []);

  const getSvgBounds = () => {
    const currentSvgContent = allFloorsData[floor]?.svgText;
    if (!currentSvgContent) return null;
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(currentSvgContent, "image/svg+xml");
      const svgElement = doc.querySelector("svg");
      
      if (svgElement) {
        const viewBox = svgElement.getAttribute("viewBox");
        if (viewBox) {
          const [x, y, width, height] = viewBox.split(" ").map(Number);
          return {
            x, y, width, height,
            bounds: [[y, x], [y + height, x + width]]
          };
        }
      }
    } catch (error) {
      console.warn("⚠️ SVG bounds parsing failed:", error.message);
    }
    
    return null;
  };

  const getCombinedSvgContent = useCallback(() => {
    const floorData = allFloorsData[floor];
    if (!floorData) return "";
    
    const { svgText, iconsText, roomData } = floorData;
    let combinedSvg = svgText || "";
    if ((floor === 'F1' || floor === 'S1' || floor === 'F3') && iconsText) {
      combinedSvg = combineSvgWithIcons(combinedSvg, iconsText);
    }
    if (roomData && roomData.rooms) {
      combinedSvg = combineSvgWithLabels(combinedSvg, roomData.rooms);
    }
    
    return combinedSvg;
  }, [floor, allFloorsData, combineSvgWithIcons, combineSvgWithLabels]);

  return {
    svgContent: getCombinedSvgContent(),
    roomData: allFloorsData[floor]?.roomData || { rooms: {} },
    rooms,
    isLoaded: isFullyLoaded,
    error,
    allFloorsData,
    isFullyLoaded,
    getRoomColor,
    getRoomById,
    getRoomsByStatus,
    getRoomsByType,
    getStats,
    applySvgColors,
    applyLabelColors,
    makePathsVisible,
    getSvgBounds,
    highlightRoom,
    unhighlightRoom,
    adjustColorBrightness,
    refreshRoomData,
    reload: () => setReloadToken((t) => t + 1)
  };
};

export default useFloorPlan;
